/**
 * Tenant API Types & Schemas
 * Defines DTOs, Zod schemas for validation, and API endpoints
 */

import { z } from '@spcc/core'

// ===========================================
// Zod Schemas
// ===========================================

export const tenantStatusDtoSchema = z.enum(['pending', 'active', 'inactive', 'suspended'])

export const tenantChannelDtoSchema = z.enum(['b2b', 'ecommerce'])

export const customerGroupDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
})

export const attachmentDtoSchema = z.object({
  name: z.string(),
  link: z.string(),
})

export const tenantDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  legalName: z.string().nullable().optional(),
  taxId: z.string().nullable().optional(),
  vatNumber: z.string().nullable().optional(),
  registrationNumber: z.string().nullable().optional(),
  tenantStatus: tenantStatusDtoSchema,
  channel: tenantChannelDtoSchema,
  telephone: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  houseNumber: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  attachments: z.array(attachmentDtoSchema).optional(),
  customerGroup: customerGroupDtoSchema.nullable().optional(),
  country: z.string().nullable().optional(),
  sapCustomerGroup: z.string().nullable().optional(),
  sapBusinessPartnerNumber: z.string().nullable().optional(),
  usersCount: z.number().int().nonnegative().optional(),
  lastSalesOrderCreatedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

// ===========================================
// Request Schemas
// ===========================================

export const tenantListQuerySchema = z.object({
  status: tenantStatusDtoSchema.optional(),
  channel: tenantChannelDtoSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().max(100).optional(),
})

export const createTenantRequestSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  legalName: z.string().optional(),
  taxId: z.string().optional(),
  vatNumber: z.string().optional(),
  registrationNumber: z.string().optional(),
  channel: tenantChannelDtoSchema,
  customerGroupId: z.number().optional(),
})

export const updateTenantRequestSchema = z.object({
  code: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  legalName: z.string().optional(),
  taxId: z.string().optional(),
  vatNumber: z.string().optional(),
  registrationNumber: z.string().optional(),
  channel: tenantChannelDtoSchema.optional(),
  customerGroupId: z.number().optional(),
  tenantStatus: tenantStatusDtoSchema.optional(),
})

export const updateTenantStatusRequestSchema = z.object({
  status: tenantStatusDtoSchema,
})

export const approveTenantRequestSchema = z.object({
  unblockUsers: z.boolean().optional(),
  customerGroupDocumentId: z.string().optional(),
})

// ===========================================
// Response Schemas (Strapi format)
// ===========================================

export const strapiPaginationSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  pageCount: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
})

export const strapiTenantListResponseSchema = z.object({
  data: z.array(tenantDtoSchema),
  meta: z.object({
    pagination: strapiPaginationSchema,
  }),
})

// Legacy format (for backwards compatibility)
export const tenantListResponseSchema = z.object({
  data: z.array(tenantDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
})

export const tenantStatsResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  suspended: z.number().int().nonnegative(),
})

// ===========================================
// Inferred Types
// ===========================================

export type TenantStatusDto = z.infer<typeof tenantStatusDtoSchema>
export type TenantChannelDto = z.infer<typeof tenantChannelDtoSchema>
export type CustomerGroupDto = z.infer<typeof customerGroupDtoSchema>
export type TenantDto = z.infer<typeof tenantDtoSchema>

export type TenantListQuery = z.infer<typeof tenantListQuerySchema>
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>
export type UpdateTenantRequest = z.infer<typeof updateTenantRequestSchema>
export type UpdateTenantStatusRequest = z.infer<typeof updateTenantStatusRequestSchema>
export type ApproveTenantRequest = z.infer<typeof approveTenantRequestSchema>

export type TenantListResponse = z.infer<typeof tenantListResponseSchema>
export type StrapiTenantListResponse = z.infer<typeof strapiTenantListResponseSchema>
export type StrapiPagination = z.infer<typeof strapiPaginationSchema>
export type TenantStatsResponse = z.infer<typeof tenantStatsResponseSchema>

// ===========================================
// Validation Helpers
// ===========================================

export function validateTenantDto(data: unknown): TenantDto {
  return tenantDtoSchema.parse(data)
}

export function validateTenantListResponse(data: unknown): TenantListResponse {
  return tenantListResponseSchema.parse(data)
}

export function validateTenantStatsResponse(data: unknown): TenantStatsResponse {
  return tenantStatsResponseSchema.parse(data)
}

// ===========================================
// API Endpoints
// ===========================================

export const TENANT_ENDPOINTS = {
  LIST: '/api/tenants',
  DETAIL: (id: string) => `/api/tenants/${id}`,
  CREATE: '/api/tenants',
  UPDATE: (id: string) => `/api/tenants/${id}`,
  DELETE: (id: string) => `/api/tenants/${id}`,
  APPROVE: (id: string) => `/api/tenants/${id}/approve`,
  SUSPEND: (id: string) => `/api/tenants/${id}/suspend`,
  UPDATE_STATUS: (id: string) => `/api/tenants/${id}/status`,
  STATS: '/api/tenants/stats',
} as const
